/**
 * Cliente HTTP tipado (app.md §5.1).
 *
 * - Injeta headers de auth (id/email/source) a partir dos cookies.
 * - Base URL vinda da marca (config/brand.ts) — sem ofuscação.
 * - Cada request tem seu próprio controller/signal (resolve a corrida do `getAjax` global, R9).
 * - Normaliza respostas inconsistentes do backend em `ApiResult<T>` (R6).
 *
 * REGRA (§15.7): nenhum componente faz fetch/XHR direto — tudo passa por aqui + hooks React Query.
 */
import { getBrand } from '../config/brand';
import { authHeaders } from './auth';
import { contextHeaders } from './requestContext';
import { watchdogHeaders } from './watchdog';
import { DEBUG } from '../config/env';
import type { ApiResult, RequestOptions, ApiErrorCode, V2ApiErrorCode } from '../types/api';

/**
 * Avisado quando um pedido falha por rede ou com 5xx (registro de uso: `error.api`). Fica como
 * gancho para o apiClient não depender do registro de uso, que por sua vez usa o apiClient.
 */
export type ApiFailureReporter = (failure: {
  path: string;
  status: number;
  durationMs: number;
  code?: string;
}) => void;

let failureReporter: ApiFailureReporter | null = null;

export function setApiFailureReporter(reporter: ApiFailureReporter | null): void {
  failureReporter = reporter;
}

function reportFailure(path: string, status: number, startedAt: number, code?: string): void {
  if (!failureReporter) return;
  try {
    failureReporter({ path, status, durationMs: Math.max(0, Math.round(Date.now() - startedAt)), ...(code ? { code } : {}) });
  } catch {
    /* o registro de uso nunca derruba o pedido */
  }
}

const V2_ERROR_CODES = new Set<V2ApiErrorCode>([
  'invalid_request',
  'unauthenticated',
  'expired',
  'entitlement',
  'inactive',
  'blocked',
  'not_found',
  'version_conflict',
  'limit_reached',
  'too_large',
  'rate_limited',
  'internal',
  'unavailable',
  'invalid_address',
  'in_catalog',
  'duplicate',
  'device_required',
  'device_paused',
]);

export interface V2ApiError {
  code: V2ApiErrorCode;
  message: string;
  details?: unknown;
}

/** Lê somente o envelope normativo `{error:{code,message,details?}}` da `/api/v2`. */
export function readV2Error(payload: unknown): V2ApiError | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const error = (payload as Record<string, unknown>)['error'];
  if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
  const record = error as Record<string, unknown>;
  if (typeof record['code'] !== 'string' || !V2_ERROR_CODES.has(record['code'] as V2ApiErrorCode)) {
    return null;
  }
  if (typeof record['message'] !== 'string' || record['message'].length === 0) return null;
  return {
    code: record['code'] as V2ApiErrorCode,
    message: record['message'],
    ...('details' in record ? { details: record['details'] } : {}),
  };
}

export function buildApiUrl(path: string, adminPrefix = false): string {
  const base = getBrand().apiBase;
  let clean = path.startsWith('/') ? path.slice(1) : path;
  // Rotas "estilo extensão" (search/new_list/content/popup) vivem sob /api/ no host admin
  // (legado: `if (sandbox) api_url += "api/"` [application.js:9521]). Em produção (api.*) não.
  if (adminPrefix && base.includes('//admin.')) {
    clean = 'api/' + clean;
  }
  return base + clean;
}

/** Resposta bruta para protocolos que não são JSON único (ex.: SSE via fetch streaming).
 * Continua centralizando URL, identidade e contexto; componentes não fazem fetch direto. */
export async function apiRawResponse(
  path: string,
  config: Pick<ApiRequestConfig, 'headers' | 'signal' | 'authHeaderStyle' | 'adminPrefix'> = {},
): Promise<Response> {
  const {
    headers = {},
    signal,
    authHeaderStyle = 'app',
    adminPrefix = false,
  } = config;
  return fetch(buildApiUrl(path, adminPrefix), {
    method: 'GET',
    headers: {
      ...authHeaders(authHeaderStyle),
      ...contextHeaders(),
      ...watchdogHeaders(path),
      ...headers,
    },
    signal,
    credentials: 'omit',
  });
}

/** Mapeia payloads de erro conhecidos do backend para um código normalizado. */
function mapKnownError(payload: unknown): ApiErrorCode | null {
  if (!payload || typeof payload !== 'object') return null;
  const obj = payload as Record<string, unknown>;
  const raw = String(obj['msg'] ?? obj['error'] ?? obj['erro'] ?? '').toLowerCase();
  if (!raw) return null;
  if (raw.includes('user_not_found') || raw.includes('not found')) return 'user_not_found';
  if (raw.includes('search_not_found')) return 'search_not_found';
  if (raw.includes('expired')) return 'expired';
  if (raw.includes('blocked')) return 'blocked';
  return 'unknown';
}

/** true quando o corpo é "vazio" no sentido do legado (`{}` sem chaves úteis ou `[]` vazio). */
function isEmptyBody(payload: unknown): boolean {
  if (payload == null) return true;
  if (Array.isArray(payload)) return payload.length === 0;
  if (typeof payload === 'object') return Object.keys(payload as object).length === 0;
  return false;
}

export interface ApiRequestConfig extends RequestOptions {
  /** Se true, corpo vazio (`{}`/`[]`) é tratado como sucesso (não como erro). */
  allowEmpty?: boolean;
  /** Rotas estilo-extensão (search/new_list/content/popup): prefixa `api/` no host admin. */
  adminPrefix?: boolean;
  /** Entrega o payload de erro do backend ao chamador (signup/purchase tratam `{ error }`
   * caso a caso — reinstall/invalid_code/…) em vez de mapear para um código genérico. */
  rawErrors?: boolean;
}

export async function apiRequest<T>(path: string, config: ApiRequestConfig = {}): Promise<ApiResult<T>> {
  const {
    method = 'GET',
    body,
    rawBody,
    headers = {},
    signal,
    authHeaderStyle = 'app',
    allowEmpty = false,
    adminPrefix = false,
    rawErrors = false,
    keepalive,
  } = config;

  if (body !== undefined && rawBody !== undefined) {
    throw new Error('apiRequest não aceita body e rawBody na mesma chamada.');
  }

  // Ordem: base → auth (source/email/id) → contexto (fingerprint, em TODA request,
  // legado [1991]) → headers específicos da chamada (ex.: os extras do /me). Os últimos
  // vencem, então uma chamada pode sobrescrever o contexto se precisar.
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...authHeaders(authHeaderStyle),
    ...contextHeaders(),
    // Watchdog: o computador e a sessão dele, só nas rotas api/v2/.
    ...watchdogHeaders(path),
    ...headers,
  };
  if (body !== undefined) finalHeaders['Content-Type'] = 'application/json';

  const startedAt = Date.now();
  let res: Response;
  try {
    res = await fetch(buildApiUrl(path, adminPrefix), {
      method,
      headers: finalHeaders,
      body: rawBody ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal,
      credentials: 'omit',
      ...(keepalive ? { keepalive: true } : {}),
    });
  } catch (err) {
    if (DEBUG) console.warn('[api] network error', path, err);
    // Pedido cancelado pela própria tela não é falha da API.
    if (!(err instanceof DOMException && err.name === 'AbortError')) reportFailure(path, 0, startedAt, 'network');
    return { ok: false, code: 'network', message: String(err) };
  }

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Alguns endpoints (event/pixel) devolvem texto/vazio; trata como sucesso vazio.
      payload = null;
    }
  }

  if (!res.ok) {
    const v2Error = readV2Error(payload);
    if (res.status >= 500) reportFailure(path, res.status, startedAt, v2Error?.code);
    if (v2Error) {
      return {
        ok: false,
        code: v2Error.code,
        status: res.status,
        message: v2Error.message,
        ...(v2Error.details !== undefined ? { details: v2Error.details } : {}),
      };
    }
    return { ok: false, code: 'http', status: res.status, message: `HTTP ${res.status}` };
  }

  const knownError = rawErrors ? null : mapKnownError(payload);
  if (knownError) {
    return { ok: false, code: knownError, message: JSON.stringify(payload) };
  }

  if (!allowEmpty && isEmptyBody(payload)) {
    return { ok: false, code: 'empty' };
  }

  return { ok: true, data: payload as T };
}

/** Açúcares por método. */
export const api = {
  get: <T>(path: string, cfg?: ApiRequestConfig) => apiRequest<T>(path, { ...cfg, method: 'GET' }),
  post: <T>(path: string, body?: unknown, cfg?: ApiRequestConfig) =>
    apiRequest<T>(path, { ...cfg, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, cfg?: ApiRequestConfig) =>
    apiRequest<T>(path, { ...cfg, method: 'PUT', body }),
  del: <T>(path: string, body?: unknown, cfg?: ApiRequestConfig) =>
    apiRequest<T>(path, { ...cfg, method: 'DELETE', body }),
};

/** Desembrulha um ApiResult, lançando em erro — para uso dentro de queryFn do React Query. */
export function unwrap<T>(result: ApiResult<T>): T {
  if (result.ok) return result.data;
  const err = new Error(result.message ?? result.code) as Error & { code?: string };
  err.code = result.code;
  throw err;
}
